<template>
  <div
    id="app"
    class="position-relative"
  >
    <BHeader
      :service="translatedLabel('brandingName', 'shared_Header')"
      :logout-label="translatedLabel('logoutLabel', 'shared_Header')"
      :current-region-label="translatedLabel('currentRegion', 'shared_Regions')"
      :menu-items="menuItems"
      :user="user as any"
      :regions="regions"
      @logout="logout"
      @change-region="setRegion($event, true, true)"
    />
    <StickyNotificationBar />
    <router-view
      :user="user"
      @authenticated="authenticated"
    />
    <div
      v-if="loading"
      class="bc-spinner"
    />
    <RegionSelectorModal
      v-if="showRegionModal"
      :regions="regions"
      :error="regionError"
      @select="setRegion($event, false, true)"
    />
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch, getCurrentInstance} from 'vue'
import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'
import BHeader from '@dxp-common-components-updated/header'
import RegionSelectorModal from '@/components/ui/RegionSelectorModal.vue'
import appConfig from '@/config'
import axios from '@/plugin/axios'
import api from '@/api'
import {dpmmService} from '@/services'
import fakeUser from './plugin/fakeUser'
import StickyNotificationBar from '@/components/NotificationBar/StickyNotificationBar.vue'
import {signOut} from '@dxp-common-components-updated/utils-global'
import constants from '@/constants'
import {useCommon} from './composables/useCommon'

const initMenuElements: string[] = ['myMachine']
const store = useStore()
const route = useRoute()
const router = useRouter()
const instance = getCurrentInstance()
const {labels, translate, translatedLabel, updateUrlParam} = useCommon()

// Reactive data
const loading = ref(false)
const _error = ref<any>(null)
const user = ref<{name: string; [key: string]: any}>({name: ''})
const menuElements = ref<string[]>([...initMenuElements])
const availableRegions = ref<string[]>([])
const showRegionModal = ref(false)
const regionError = ref(false)
const getUserPromise = ref<Promise<any> | null>(null)

// Store getters
const getAppReady = computed(() => store.getters['mymachine/getAppReady'])
const isSetRegion = computed(() => store.getters['mymachine/isSetRegion'])
const getRegion = computed(() => store.getters['mymachine/getRegion'])
const getRegionalizedFeatures = computed(() => store.getters['mymachine/getRegionalizedFeatures'])
const getLanguage = computed(() => store.getters['mymachine/getLanguage'])
const getIdentity = computed(() => store.getters['mymachine/getIdentity'])
const languageList = computed(() => store.getters['mymachine/getAvailableLanguages'])
const getCurrentSerial = computed(() => store.getters['mymachine/getCurrentSerial'])

// Store mutations
function saveRegion(payload: any): void { store.commit('mymachine/saveRegion', payload) }
function removeSerialNumber(serial: any): void { store.commit('mymachine/removeSerialNumber', serial) }
function saveFeatures(features: Record<string, boolean>): void { store.commit('mymachine/saveFeatures', features) }
function saveLanguage(lang: string): void { store.commit('mymachine/saveLanguage', lang) }

// Computed
const regions = computed(() => {
  return availableRegions.value.map((conceptKey: string) => ({
    key: conceptKey,
    translation: translatedLabel(conceptKey, 'shared_RegionNames'),
    active: getRegion.value === conceptKey
  }))
})

const menuItems = computed(() => {
  let items: any[] = []
  const {items: troubleshootingItems, topLevelItem} = getTroubleshootingItems()
  for (const i of menuElements.value) {
    if (i === constants.TROUBLESHOOTING_ADMIN || (i === constants.MY_OPEN_SESSIONS && getRegionalizedFeatures.value.troubleshootingAdmin)) {
      continue
    }
    if (i === constants.MY_OPEN_SESSIONS && topLevelItem) {
      items.push(topLevelItem)
      continue
    }
    const iconConcept = i + 'Icon'
    const titleConcept = i + 'Title'
    const pathConcept = i + 'Path'
    items.push({
      icon: translate(iconConcept, labels.value, 'shared_HeaderMenu'),
      title: translate(titleConcept, labels.value, 'shared_HeaderMenu'),
      path: translate(pathConcept, labels.value, 'shared_HeaderMenu')
    })
  }
  if (troubleshootingItems.length > 0) {
    const dealershipMenu = {
      icon: translate('dealershipIcon', labels.value, 'shared_HeaderMenu'),
      title: translate('dealershipTitle', labels.value, 'shared_HeaderMenu'),
      items: troubleshootingItems
    }
    const supportIndex = items.findIndex((item: any) =>
      item.title === translate('supportTitle', labels.value, 'shared_HeaderMenu')
    )
    if (supportIndex !== -1) {
      items.splice(supportIndex, 0, dealershipMenu)
    } else {
      items.push(dealershipMenu)
    }
  }
  return items
})

// Methods
async function loadApp(): Promise<void> {
  await loadFeatureFlags(getCurrentSerial.value)
  getRegions()
  store.dispatch('mymachine/fetchIdentity')
}

function detectLanguage(): void {
  if (!getLanguage.value && getIdentity.value && languageList.value && getRegion.value) {
    let lang = 'en'
    const navigatorLanguages: readonly string[] = (navigator.languages && navigator.languages.length) ? navigator.languages : ((navigator as any).userLanguage ? [(navigator as any).userLanguage] : (navigator.language ? [navigator.language] : ((navigator as any).browserLanguage ? [(navigator as any).browserLanguage] : ['en'])))
    for (let navigatorLanguage of navigatorLanguages) {
      navigatorLanguage = navigatorLanguage.substr(0, 2)
      if (navigatorLanguage && languageList.value.indexOf(navigatorLanguage) !== -1) {
        lang = navigatorLanguage
        break
      }
    }
    const identityLanguage = getIdentity.value.language?.toLowerCase()
    if (identityLanguage && languageList.value.indexOf(identityLanguage) !== -1) lang = identityLanguage
    saveLanguage(lang)
  }
}

function checkFeatureFlag(): void {
  if (getRegionalizedFeatures.value.mymachineApp) {
    if (route.path === '/error') router.push('/')
  } else {
    router.push({name: 'Error', query: {'redirect': 'false', 'error': 'featureUnauthorizedNoRedirect'}})
  }
}

function checkWebroles(): boolean {
  const auth = instance?.appContext.config.globalProperties.$auth
  if (!auth) return false
  const token = auth.getAccessToken()
  if (!token) return false
  const decoded = auth.token.decode(token)
  if (!(decoded?.payload && 'passportWebRoles' in decoded.payload)) {
    router.push({name: 'Error', query: {'error': 'noPassportWebrolesError'}})
    return false
  }
  return true
}

function authenticated(): void {
  if (!user.value.name) {
    if (checkWebroles()) {
      const auth = instance?.appContext.config.globalProperties.$auth
      if (!auth) return
      auth.getUser().then((e: any) => {
        user.value = e
        store.commit('mymachine/saveUser', user.value)
      }).catch(() => {})
    }
  }
}

function getUser(): void {
  const auth = instance?.appContext.config.globalProperties.$auth
  if (!auth) return
  const isCallbackPath = window.location.pathname.includes('implicit/callback')
  if (getUserPromise.value === null && !isCallbackPath) {
    getUserPromise.value = auth.getUser()
    getUserPromise.value!.then((e: any) => {
      if (checkWebroles()) {
        user.value = e
        store.commit('mymachine/saveUser', user.value)
      }
    }).catch(async () => {
      if (!auth.isLoginRedirect()) {
        await auth.signInWithRedirect({originalUri: `${ window.location.pathname.replace(/^\/service/, '')}${window.location.search}`})
      }
    })
  }
}

async function loadFeatureFlags(serialNumber = getCurrentSerial.value): Promise<void> {
  await axios.get(api.endpoints.DPMM_API.FEATURES, {
    params: {
      serial_number: serialNumber
    }
  }).then((r: any) => {
    const features = r.data.filter((f: any) => f.enabled).reduce((obj: Record<string, boolean>, f: any) => {
      obj[f.name] = true
      return obj
    }, {})
    saveFeatures(features)
  }).catch(() => {
    router.push({name: 'Error', query: {'redirect': 'false', 'error': 'featureUnauthorizedNoRedirect'}})
  })
}

async function getRegions(): Promise<void> {
  try {
    const {data} = await dpmmService.fetchAvailableRegions()
    availableRegions.value = data.business_units

    const regionFromQuery = route.query[constants.REGION_QUERY_NAME] as string
    const shouldRedirect = regionFromQuery !== getRegion.value && route.path !== '/implicit/callback'

    if (availableRegions.value.length === 0) {
      router.push({name: 'Error', query: {'error': 'noRegionsError'}})
    } else if (availableRegions.value.length === 1) {
      const shouldUpdateQuery = regionFromQuery && regionFromQuery === availableRegions.value[0] ? false : true
      setRegion(availableRegions.value[0], shouldRedirect, shouldUpdateQuery)
    } else if (regionFromQuery) {
      setRegion(regionFromQuery, shouldRedirect)
    } else if (!isSetRegion.value) {
      showRegionModal.value = true
    }
  } catch (err) {
    console.log('regionsError', err)
    router.push({name: 'Error', query: {'error': 'regionsError'}})
  }
}

function setRegion(region: string, redirect = false, updateQuery = false): void {
  regionError.value = false
  const regionUpperCase = region.toUpperCase()
  if (availableRegions.value.includes(regionUpperCase)) {
    saveRegion({region: regionUpperCase, redirectToDashboard: redirect})
    const currentSerial = getCurrentSerial.value
    if (currentSerial)
      dpmmService.fetchDetails(currentSerial, regionUpperCase).then(({data}: any) => {
        if (data.serial_number !== currentSerial) {
          removeSerialNumber(currentSerial)
          updateUrlParam('serial', getCurrentSerial.value)
        }
      }).catch(() => {
        removeSerialNumber(currentSerial)
        updateUrlParam('serial', getCurrentSerial.value)
      })
    if (updateQuery && !redirect) {
      setTimeout(() => {
        updateUrlParam('serial', currentSerial)
        updateUrlParam(constants.REGION_QUERY_NAME, regionUpperCase)
      }, 1000)
    }
  } else {
    regionError.value = true
    showRegionModal.value = true
  }
}

async function logout(): Promise<void> {
  const auth = instance?.appContext.config.globalProperties.$auth
  if (auth) {
    await signOut.call({$auth: auth})
  }
}

function getTroubleshootingItems(): { items: any[]; topLevelItem: any } {
  const items: any[] = []
  for (const i of menuElements.value) {
    if (i === constants.TROUBLESHOOTING_ADMIN) {
      const title = translate(i + 'Title', labels.value, 'shared_HeaderMenu')
      const path = translate(i + 'Path', labels.value, 'shared_HeaderMenu')
      items.push({title, path: `${path}?${constants.REGION_QUERY_NAME}=${getRegion.value}`})
    } else if (i === constants.MY_OPEN_SESSIONS) {
      const title = translate(i + 'Title', labels.value, 'shared_HeaderMenu')
      const path = translate(i + 'Path', labels.value, 'shared_HeaderMenu')
      if (getRegionalizedFeatures.value.troubleshootingAdmin) {
        items.push({title, path: `${path}?${constants.REGION_QUERY_NAME}=${getRegion.value}`})
      } else {
        return {
          items: [],
          topLevelItem: {
            icon: translate(i + 'Icon', labels.value, 'shared_HeaderMenu'),
            title,
            path: `${path}?${constants.REGION_QUERY_NAME}=${getRegion.value}`
          }
        }
      }
    }
  }
  return {items, topLevelItem: null}
}

// Watchers
watch(() => route.name, (name) => {
  loading.value = name === null || name === 'ImplicitCallback'
})

watch(getRegionalizedFeatures, (flags: any) => {
  let newMenuElements = initMenuElements.slice()
  if (flags.libraryApp) newMenuElements.push('serviceLibrary')
  if (flags.softwareApp) newMenuElements.push('software')
  if (flags.videosApp) newMenuElements.push('videos')
  if (flags.myOpenSession) newMenuElements.push(constants.MY_OPEN_SESSIONS)
  if (flags.troubleshootingAdmin) newMenuElements.push(constants.TROUBLESHOOTING_ADMIN)
  if (flags.supportApp) newMenuElements.push('support')
  menuElements.value = newMenuElements
  if (Object.keys(flags).length > 0 && isSetRegion.value) checkFeatureFlag()
})

watch(getAppReady, async (ready: boolean) => {
  if (ready) {
    loadApp()
  }
}, {immediate: true})

watch(getRegion, (newRegion: string, oldRegion: string) => {
  if (newRegion && Object.keys(getRegionalizedFeatures.value).length > 0) checkFeatureFlag()
  if (newRegion) detectLanguage()
  store.dispatch('mymachine/fetchRegionConcepts', newRegion)

  document.body.classList.remove(oldRegion)
  document.body.classList.add(newRegion)
})

watch(getIdentity, (identity: any) => {
  if (identity) detectLanguage()
})

watch(languageList, (language: any) => {
  if (language) detectLanguage()
})

watch(getCurrentSerial, (newSerial: string, oldSerial: string) => {
  if (!newSerial || newSerial === oldSerial) return
  loadFeatureFlags(newSerial)
})

// Created logic
loading.value = !route.name || route.name === 'ImplicitCallback'
store.dispatch('mymachine/fetchRegionConcepts', 'NA')
const auth = instance?.appContext.config.globalProperties.$auth
if (!auth) {
  user.value = fakeUser
  store.commit('mymachine/saveUser', user.value)
} else {
  getUser()
}

// Navigate to app root only when user loads the bare app URL.
const isCallbackPath = window.location.pathname.includes('implicit/callback')
const publicPath = (import.meta.env.VITE_PUBLIC_PATH || '').replace(/\/$/, '')
const normalizedPathname = window.location.pathname.replace(/\/$/, '')
const isBareAppPath = normalizedPathname === publicPath || normalizedPathname === ''
if (!isCallbackPath && isBareAppPath) {
  router.push('/mymachine/').catch(() => {})
}

switch (appConfig.nodeEnv) {
case 'development':
  if (import.meta.env.VITE_PREFIX)
    document.title = import.meta.env.VITE_PREFIX + ' My Machine'
  else
    document.title = (import.meta.env.VITE_OFFLINE === 'true' ? '[PREV] ' : '[DEV] ') + 'My Machine'
  break
case 'int':
  document.title = '[INT] My Machine'
  break
case 'qa':
  document.title = '[QA] My Machine'
  break
}
</script>

<style lang="scss">
@import './scss/grid';

html {
  height: 100%;
}

body {
  min-height: 100%;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

#onetrust-consent-sdk {
  display: none;
}
.EMEA #onetrust-consent-sdk{
  display: block;
}
</style>
